<template>
  <Drawer
    :open="open"
    :title="t('journey.title')"
    :subtitle="t('journey.subtitle')"
    @close="$emit('close')"
  >
    <Spinner v-if="loading" :label="t('journey.loadingVariants')" />

    <template v-else>
      <!-- Effacer en position haute (dès qu'un départ est posé). -->
      <div v-if="selDeparture" class="fg-od__toolbar">
        <button type="button" class="fg-od__clear" @click="clear">{{ t('journey.clear') }}</button>
      </div>

      <!-- En-tête De / Vers : masqué tant qu'aucun départ n'est choisi (le bloc
           ferait doublon avec la liste ci-dessous). -->
      <div v-if="selDeparture" class="fg-od">
        <button
          type="button"
          class="fg-od__field"
          :class="{ 'is-active': picking && active === 'departure' }"
          @click="openField('departure')"
        >
          <span class="fg-od__field-label">{{ t('journey.departureLabel') }}</span>
          <span class="fg-od__field-value">{{ portLabel(selDeparture) }}</span>
        </button>

        <button
          type="button"
          class="fg-od__field"
          :class="{ 'is-active': picking && active === 'destination' }"
          @click="openField('destination')"
        >
          <span class="fg-od__field-label">{{ t('journey.destinationLabel') }}</span>
          <span class="fg-od__field-value" :class="{ 'is-empty': !selDestination }">
            {{ selDestination ? portLabel(selDestination) : t('journey.choosePort') }}
          </span>
        </button>

        <button
          v-if="reverseExists"
          type="button"
          class="fg-od__swap"
          :aria-label="t('journey.swap')"
          @click="swap"
        ><Icon name="swap" :size="16" /></button>
      </div>

      <!-- Retour auto, mis en évidence (chip). -->
      <div v-if="bothSelected && reverseExists" class="fg-od__return">
        <Icon name="arrow-left" :size="14" />
        <span class="fg-od__return-label">{{ t('journey.returnLabel') }}</span>
        <span class="fg-od__return-route">{{ portLabel(selDestination!) }} → {{ portLabel(selDeparture!) }}</span>
      </div>

      <!-- Liste : uniquement en mode sélection (masquée une fois les 2 ports
           choisis → plus d'alternatives déroutantes). -->
      <div v-if="picking" class="fg-od__picker">
        <p v-if="!selDeparture" class="fg-od__picker-label">{{ t('journey.stepDeparture') }}</p>

        <!-- Recherche affichée seulement pour le DÉPART (liste de tous les ports
             d'origine, potentiellement longue). Pour la DESTINATION, la liste se
             limite aux ports atteignables depuis le départ choisi → courte, pas
             besoin de filtre. -->
        <input
          v-if="active === 'departure'"
          v-model="query"
          type="text"
          class="fg-search-input"
          :placeholder="t('journey.searchPort')"
        />

        <p v-if="groups.length === 0" class="fg-empty">{{ t('journey.noPortFound') }}</p>

        <div v-else class="fg-od__groups">
          <section v-for="g in groups" :key="g.country" class="fg-od__group">
            <h3 class="fg-od__group-title">{{ g.country }}</h3>
            <ul class="fg-veh__list">
              <li
                v-for="p in g.ports"
                :key="p.port_code"
                class="fg-row fg-od__port"
                :class="{ 'is-selected': isPortSelected(p) }"
                @click="choosePort(p)"
              >
                <div class="fg-od__port-main">
                  <span class="fg-od__port-name">{{ portLabel(p) }}</span>
                  <span v-if="active === 'destination' && carriersFor(p)" class="fg-jrn__carrier">{{ carriersFor(p) }}</span>
                </div>
                <Icon name="chevron-right" :size="16" />
              </li>
            </ul>
          </section>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="fg-drawer__actions">
        <button
          class="fg-btn fg-btn--primary fg-btn--block"
          type="button"
          :disabled="!bothSelected"
          @click="$emit('close')"
        >{{ t('common.continue') }}</button>
        <button
          class="fg-btn fg-btn--secondary fg-btn--block"
          type="button"
          :disabled="!bothSelected"
          @click="$emit('next')"
        >{{ t('journey.continueToDates') }}</button>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useApi } from '../../api'
import { useI18n } from '../../i18n'
import { useStore } from '../../store'
import { Direction } from '../../../../shared/types/enums'
import type { Journey, Port } from '../../../../shared/types'
import { humanizeLabel } from '../../utils/format'
import Drawer from '../Drawer.vue'
import Icon from '../Icon.vue'
import Spinner from '../atoms/Spinner.vue'

type Field = 'departure' | 'destination'

interface PortGroup { country: string; ports: Port[] }

const props = defineProps<{ open: boolean }>()
defineEmits<{ close: []; next: [] }>()

const api = useApi()
const store = useStore()
const { t, locale } = useI18n()

const localCatalog = ref<Journey[]>([])
const loading = ref(false)
const active = ref<Field>('departure')
const picking = ref(true) // true = liste affichée (sélection en cours) ; false = résumé
const query = ref('')
const selDeparture = ref<Port | null>(null)
const selDestination = ref<Port | null>(null)

// Source de vérité : le catalogue pré-chargé du store (instant à l'ouverture).
// Fallback `localCatalog` uniquement si le pré-load a échoué.
const catalog = computed<Journey[]>(() => store.journeyCatalog.length > 0 ? store.journeyCatalog : localCatalog.value)

watch(() => props.open, async (isOpen) => {
  if (!isOpen) return
  if (catalog.value.length === 0) await loadJourneys()
  query.value = ''
  initFromStore()
  active.value = selDeparture.value && !selDestination.value ? 'destination' : 'departure'
  // Réouverture avec une route déjà choisie → on montre le résumé, pas la liste.
  picking.value = !bothSelected.value
})

onMounted(() => { if (props.open && catalog.value.length === 0) void loadJourneys() })

async function loadJourneys(): Promise<void> {
  loading.value = true
  try { localCatalog.value = await api.listJourneys(locale.value) }
  catch (err) { store.error = { code: 'JOURNEYS_LOAD_FAILED', message: err instanceof Error ? err.message : 'Failed to load routes' } }
  finally { loading.value = false }
}

// Reflète la sélection courante du store (cas réouverture / préfill journey-id).
function initFromStore(): void {
  const primary = store.search.journeys.find((j) => j.direction === Direction.Outward) ?? store.search.journeys[0]
  selDeparture.value = primary?.origin ?? null
  selDestination.value = primary?.destination ?? null
}

// ── État dérivé ─────────────────────────────────────────────────────────
const bothSelected = computed(() => !!selDeparture.value && !!selDestination.value)

// Le retour n'existe que si la route inverse (dest → départ) est desservie.
const reverseExists = computed(() =>
  bothSelected.value
  && isReachable(selDestination.value!.port_code, selDeparture.value!.port_code),
)

// Ports proposés selon le champ actif, filtrés par la recherche puis groupés par pays.
const groups = computed<PortGroup[]>(() => {
  const source = active.value === 'departure'
    ? uniquePorts(catalog.value.map((j) => j.origin))
    : selDeparture.value
      ? uniquePorts(
          catalog.value
            .filter((j) => j.origin?.port_code === selDeparture.value!.port_code)
            .map((j) => j.destination),
        )
      : []

  const q = query.value.trim().toLowerCase()
  const filtered = q
    ? source.filter((p) => `${p.port_name} ${p.country_name}`.toLowerCase().includes(q))
    : source
  return groupByCountry(filtered)
})

// ── Helpers données ─────────────────────────────────────────────────────
function routeJourneys(depCode?: string, destCode?: string): Journey[] {
  if (!depCode || !destCode) return []
  return catalog.value.filter((j) => j.origin?.port_code === depCode && j.destination?.port_code === destCode)
}

function isReachable(depCode: string, destCode: string): boolean {
  return routeJourneys(depCode, destCode).length > 0
}

function uniquePorts(list: Array<Port | undefined>): Port[] {
  const seen = new Set<string>()
  const out: Port[] = []
  for (const p of list) {
    if (!p?.port_code || seen.has(p.port_code)) continue
    seen.add(p.port_code)
    out.push(p)
  }
  return out
}

function groupByCountry(ports: Port[]): PortGroup[] {
  const map = new Map<string, Port[]>()
  for (const p of ports) {
    const country = p.country_name || '—'
    const arr = map.get(country)
    if (arr) arr.push(p); else map.set(country, [p])
  }
  return Array.from(map.entries())
    .map(([country, list]) => ({
      country,
      ports: list.sort((a, b) => a.port_name.localeCompare(b.port_name, locale.value)),
    }))
    .sort((a, b) => a.country.localeCompare(b.country, locale.value))
}

function uniqueCarriers(journeys: Journey[]): string {
  return Array.from(new Set(journeys.map(companyName).filter(Boolean))).join(' · ')
}

function carriersFor(destPort: Port): string {
  return uniqueCarriers(routeJourneys(selDeparture.value?.port_code, destPort.port_code))
}

function companyName(journey: Journey): string {
  const c = journey.company
  if (!c) return ''
  return humanizeLabel(typeof c === 'string' ? c : (c.name ?? ''))
}

// Casse humanisée des ports (backend renvoie « NADOR » → « Nador »).
function portLabel(p: Port): string {
  return humanizeLabel(p.port_name)
}

function isPortSelected(p: Port): boolean {
  const sel = active.value === 'departure' ? selDeparture.value : selDestination.value
  return sel?.port_code === p.port_code
}

// ── Actions ─────────────────────────────────────────────────────────────
function openField(field: Field): void {
  if (field === 'destination' && !selDeparture.value) return
  active.value = field
  picking.value = true
  query.value = ''
}

function choosePort(p: Port): void {
  if (active.value === 'departure') {
    chooseDeparture(p)
  } else {
    chooseDestination(p)
  }
}

function chooseDeparture(p: Port): void {
  selDeparture.value = p
  query.value = ''
  // La destination courante peut ne plus être atteignable depuis le nouveau départ.
  if (selDestination.value && !isReachable(p.port_code, selDestination.value.port_code)) {
    selDestination.value = null
  }
  active.value = 'destination'
  picking.value = true
  if (selDestination.value) applySelection()
}

function chooseDestination(p: Port): void {
  selDestination.value = p
  query.value = ''
  picking.value = false // les 2 ports sont choisis → on montre le résumé
  applySelection()
}

function swap(): void {
  const a = selDeparture.value, b = selDestination.value
  if (!a || !b || !isReachable(b.port_code, a.port_code)) return
  selDeparture.value = b
  selDestination.value = a
  picking.value = false
  applySelection()
}

function clear(): void {
  selDeparture.value = null
  selDestination.value = null
  active.value = 'departure'
  picking.value = true
  store.search.journeys = []
  store.search.request = {}
}

// Charge les variants (Outward + Return) de TOUS les journeys de la paire
// départ/destination en parallèle. Le calendar fusionnera leurs `departure[]`.
function applySelection(): void {
  const dep = selDeparture.value, dest = selDestination.value
  if (!dep || !dest) return
  const ids = routeJourneys(dep.port_code, dest.port_code)
    .map((j) => j.id)
    .filter((id): id is string => !!id)
  if (ids.length === 0 || store.loadingJourney) return

  store.loadingJourney = true
  store.search.request = {}
  Promise.all(ids.map((id) => api.getJourneyVariants(id)))
    .then((results) => { store.search.journeys = results.flat() })
    .catch((err) => {
      store.search.journeys = []
      store.error = { code: 'JOURNEY_DETAIL_FAILED', message: err instanceof Error ? err.message : 'Failed to load route details' }
    })
    .finally(() => { store.loadingJourney = false })
}
</script>
